import { useParams } from 'react-router-dom';

import { Finding, KeyImageLabel } from '@/api';
import { useKeyImageLabelCreatorMutation, useKeyImageLabelUpdaterMutation } from '@/api/mutations';
import { formatEditorString } from '@/shared/util';
import { useDefaultFindingStore, useFindingStore, useKeyImagesStore } from '@/store';

import { generateLabelFromCondition } from './key-image-label.util';

export function useKeyImageLabel() {
  const { organ } = useParams();
  const organName = organ?.replace(/-/g, '_') as string;
  const { keyImageLabels, setKeyImageLabels } = useKeyImagesStore();
  const { getFindingsByOrgan, updateFinding } = useFindingStore();
  const { getDefaultFindingsByOrgan, updateDefaultFinding } = useDefaultFindingStore();

  const { mutate: createKeyImageLabelMutation } = useKeyImageLabelCreatorMutation();
  const { mutate: updateKeyImageLabelMutation } = useKeyImageLabelUpdaterMutation();

  const findMatchingFinding = (finding: Finding) => {
    const existingOrgan = organName || (finding.conditionTemplate.organ as string);
    const findingsByOrgan = getFindingsByOrgan(existingOrgan);

    return findingsByOrgan.find(
      (findingData) => findingData.renderedSummary.uuid === finding.renderedSummary.uuid,
    );
  };

  const findDefaultFinding = (finding: Finding) => {
    const existingOrgan = organName || (finding.conditionTemplate.organ as string);
    const defaultFindingsByOrgan = getDefaultFindingsByOrgan(existingOrgan);

    return defaultFindingsByOrgan.find(
      (findingData) => findingData.renderedSummary.uuid === finding.renderedSummary.uuid,
    );
  };

  const updateFindingKeyImages = (finding: Finding, keyImage: KeyImageLabel) => {
    const existingOrgan = organName || (finding.conditionTemplate.organ as string);

    if (
      !finding.renderedSummary.keyImages.some(
        (existingKeyImage) => existingKeyImage.uuid === keyImage.uuid,
      )
    ) {
      updateFinding(
        existingOrgan,
        {
          renderedSummary: {
            ...finding.renderedSummary,
            keyImages: [...finding.renderedSummary.keyImages, keyImage],
          },
        },
        finding.renderedSummary.uuid,
      );

      return;
    }

    updateFinding(
      existingOrgan,
      {
        renderedSummary: {
          ...finding.renderedSummary,
          keyImages: finding.renderedSummary.keyImages.map((existingKeyImage) =>
            existingKeyImage.uuid === keyImage.uuid ? keyImage : existingKeyImage,
          ),
        },
      },
      finding.renderedSummary.uuid,
    );
  };

  const getActiveKeyImageLabels = (renderedSummaryUuid?: string) => {
    const activeLabels = keyImageLabels
      .filter((label) => label.status !== 'deprecated')
      .filter((label, index, self) => index === self.findIndex((l) => l.uuid === label.uuid));

    return renderedSummaryUuid
      ? activeLabels.filter((label) => label.renderedSummaryUuid === renderedSummaryUuid)
      : activeLabels;
  };

  const handleCreateKeyImageLabel = (finding: Finding, label: string) => {
    createKeyImageLabelMutation(
      {
        label,
        renderedSummaryUuid: finding.renderedSummary.uuid,
      },
      {
        onSuccess: (data) => {
          if (finding.conditionTemplate.condition?.startsWith('default_question_')) {
            const existingDefaultFinding = findDefaultFinding(finding);

            if (existingDefaultFinding) {
              updateDefaultFinding(
                existingDefaultFinding.conditionTemplate.organ as string,
                {
                  renderedSummary: {
                    ...existingDefaultFinding.renderedSummary,
                    keyImages: [...existingDefaultFinding.renderedSummary.keyImages, data],
                  },
                },
                existingDefaultFinding.renderedSummary.uuid,
              );
            }
          } else {
            const existingFinding = findMatchingFinding(finding);

            if (existingFinding) {
              updateFindingKeyImages(existingFinding, data);
            }
          }

          setKeyImageLabels([...keyImageLabels, data]);
        },
      },
    );
  };

  const handleUpdateKeyImageLabel = (
    finding: Finding,
    keyImageUuid: string,
    payload: KeyImageLabel,
  ) =>
    new Promise((resolve) => {
      updateKeyImageLabelMutation(
        {
          keyImageUuid,
          payload,
        },
        {
          onSuccess: (data) => {
            if (data.status === 'deprecated') {
              setKeyImageLabels(keyImageLabels.filter((label) => label.uuid !== data.uuid));
            } else {
              setKeyImageLabels(
                keyImageLabels.map((label) => (label.uuid === keyImageUuid ? data : label)),
              );
            }

            if (finding.conditionTemplate.condition?.startsWith('default_question_')) {
              const existingDefaultFinding = findDefaultFinding(finding);

              if (existingDefaultFinding) {
                updateDefaultFinding(
                  existingDefaultFinding.conditionTemplate.organ as string,
                  {
                    renderedSummary: {
                      ...existingDefaultFinding.renderedSummary,
                      keyImages: existingDefaultFinding.renderedSummary.keyImages.map(
                        (existingKeyImage) =>
                          existingKeyImage.uuid === keyImageUuid ? data : existingKeyImage,
                      ),
                    },
                  },
                  existingDefaultFinding.renderedSummary.uuid,
                );
              }
            } else {
              const existingFinding = findMatchingFinding(finding);

              if (existingFinding) {
                updateFindingKeyImages(existingFinding, data);
              }
            }

            resolve(data);
          },
        },
      );
    });

  const onCreateKeyImageLabel = (finding: Finding, labelInput?: string) => {
    const label = formatEditorString(
      labelInput || generateLabelFromCondition(finding.conditionTemplate.condition) || '',
    );

    handleCreateKeyImageLabel(finding, label);
  };

  const onUpdateKeyImageLabel = async (
    finding: Finding,
    keyImageUuid: string,
    newLabel: string,
  ) => {
    if (!keyImageUuid) {
      return;
    }

    const updatedLabels = [...keyImageLabels];

    const labelIndex = updatedLabels.findIndex((label) => label.uuid === keyImageUuid);

    if (labelIndex >= 0) {
      const updatedLabel = {
        ...updatedLabels[labelIndex],
        label: newLabel,
      };

      await handleUpdateKeyImageLabel(finding, keyImageUuid, updatedLabel);
    }
  };

  const onRemoveKeyImageLabel = async (finding: Finding, labelToDelete: KeyImageLabel) => {
    if (!finding.renderedSummary.uuid || !labelToDelete.uuid) {
      return;
    }

    await handleUpdateKeyImageLabel(finding, labelToDelete.uuid, {
      ...labelToDelete,
      status: 'deprecated',
    });
  };

  return {
    getActiveKeyImageLabels,
    handleCreateKeyImageLabel,
    handleUpdateKeyImageLabel,
    onCreateKeyImageLabel,
    onRemoveKeyImageLabel,
    onUpdateKeyImageLabel,
  };
}
